// Especialidad médica del PRACTICANTE logueado (Rediseño mayor 2026-08-31,
// Módulo F) — reemplaza a useEspecialidadesHabilitadas (tenant-wide) como
// filtro REAL de qué formularios puede CREAR este médico. El hook viejo no
// se borra: sigue siendo el techo ("qué existe en absoluto para el
// tenant"), este es el filtro real por-practicante. Mismo patrón que
// useEspecialidadesHabilitadas.ts (searchOne + useEffect), leyendo el
// `PractitionerRole` que crea el registro (hce-registro.ts en la bóveda,
// vía crearPractitionerRole en medplum-admin.ts).
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { getEspecialidadDemoOverride, subscribeEspecialidadDemoOverride } from './especialidadDemoOverride';

/** Mismo system que URL_CODESYSTEM_ESPECIALIDAD en packages/web/src/api/lib/medplum-admin.ts — no se puede compartir el literal entre paquetes distintos. */
const URL_CODESYSTEM_ESPECIALIDAD = 'http://logiacare.ec/fhir/CodeSystem/especialidad-medica';

/**
 * Única cuenta habilitada para el selector de especialidad de demo (pedido
 * explícito del usuario 2026-09-01, "solo para este demo, fines
 * comerciales") — cualquier otra cuenta ignora el override por completo,
 * sigue viendo su especialidad real. Ver SelectorEspecialidadDemo.tsx.
 */
export const EMAIL_CUENTA_DEMO_ESPECIALIDAD = 'dra.demo@logiacare.com';

export type EstadoEspecialidadDelPracticante = { cargando: true } | { cargando: false; codigo: string | null };

export function useEspecialidadDelPracticante(): EstadoEspecialidadDelPracticante {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [codigo, setCodigo] = useState<string | null | undefined>(undefined);
  const esCuentaDemo = profile?.telecom?.some((t) => t.system === 'email' && t.value === EMAIL_CUENTA_DEMO_ESPECIALIDAD) ?? false;
  const override = useSyncExternalStore(subscribeEspecialidadDemoOverride, getEspecialidadDemoOverride, () => null);

  useEffect(() => {
    if (!profile?.id) {
      // Sin perfil de Practitioner (ej. todavía cargando la sesión): no hay
      // nada que buscar. `null` = "sin especialidad", no "cargando".
      setCodigo(null);
      return;
    }
    let cancelado = false;
    medplum
      .searchOne('PractitionerRole', { practitioner: `Practitioner/${profile.id}` })
      .then((role) => {
        if (cancelado) return;
        const coding = role?.specialty?.[0]?.coding?.find((c) => c.system === URL_CODESYSTEM_ESPECIALIDAD);
        setCodigo(coding?.code ?? null);
      })
      .catch(() => {
        if (!cancelado) setCodigo(null);
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, profile?.id]);

  // El override de demo gana sobre lo real, pero SOLO para la cuenta
  // habilitada — nunca toca Medplum, es puro estado de navegador.
  if (esCuentaDemo && override) {
    return { cargando: false, codigo: override };
  }

  if (codigo === undefined) {
    return { cargando: true };
  }
  return { cargando: false, codigo };
}
